import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Building2, Bell, MapPin, DollarSign, Users, Handshake } from "lucide-react";

export default function DealersSolution() {
  useEffect(() => {
    document.title = "For Dealers — LumberLoop USA";
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta");
      m.setAttribute("name", "description"); document.head.appendChild(m); return m;
    })();
    meta.setAttribute("content", "LumberLoop USA routes qualified contractor requests directly to approved dealers in your territory — sponsored by the manufacturers you already stock. Free to dealers.");
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="dealers-solution-page">
      <section className="relative bg-[#0F2540] text-white border-b-2 border-[#1A365D]">
        <div className="stripes-stars absolute inset-0 opacity-15" aria-hidden="true"/>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-20">
          <div className="label-tag text-slate-300">For Dealers · Lumberyards · Supply Houses</div>
          <h1 className="font-heading text-4xl md:text-6xl font-black uppercase mt-3 leading-[0.95]">
            Contractor leads you didn't have to <span className="text-[#C62828]">go find.</span>
          </h1>
          <p className="mt-5 text-lg text-slate-200 max-w-3xl">
            When a contractor in your territory asks for a manufacturer you carry, LumberLoop USA routes that request directly to you — free of charge, sponsored by the manufacturer. Your team quotes. Your team closes.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2">Join as a Dealer <ArrowRight size={16}/></Link>
            <a href="#how" className="btn-ghost-light inline-flex items-center gap-2">See How It Works</a>
          </div>
        </div>
        <div className="h-2 stripes-flag" aria-hidden="true"/>
      </section>

      {/* Value */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <div className="max-w-3xl">
          <div className="label-tag">Why Dealers Join</div>
          <h2 className="font-heading text-3xl md:text-5xl font-black uppercase mt-2 text-[#1A365D]">
            More quotes. <span className="text-[#C62828]">Zero cost. No new software.</span>
          </h2>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: DollarSign, title: "Free to participate", body: "Dealers never pay for LumberLoop USA. Every routing decision is sponsored by the manufacturer whose product the contractor asked about." },
            { icon: MapPin, title: "Only your territory", body: "You receive requests matched to your city, ZIP, and state. No spam, no out-of-area noise." },
            { icon: Building2, title: "Only brands you carry", body: "Manufacturers you already sell decide which of their approved dealers should receive each opportunity." },
            { icon: Bell, title: "Email notifications", body: "New requests land in your inbox with full contractor contact info, specs, quantity, and timeline. Respond directly." },
            { icon: Users, title: "Your customer, your quote", body: "LumberLoop USA doesn't price, doesn't fulfill, and doesn't take a cut. You own the customer and the transaction." },
            { icon: Handshake, title: "Stronger mfr relationships", body: "Manufacturers see which dealers respond fastest and win most — a real, data-backed reason to invest more in your partnership." },
          ].map(f => (
            <div key={f.title} className="brutal-card-flat bg-white p-5">
              <div className="w-11 h-11 bg-[#C62828] text-white flex items-center justify-center"><f.icon size={20}/></div>
              <div className="font-heading font-bold uppercase mt-3 text-[#0F2540]">{f.title}</div>
              <p className="mt-2 text-sm text-slate-700">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How */}
      <section id="how" className="bg-[#F8F9FB] border-y border-slate-200 py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="label-tag">How It Works for Dealers</div>
          <h2 className="font-heading text-3xl md:text-4xl font-black uppercase mt-2 text-[#1A365D]">Three steps. <span className="text-[#C62828]">One inbox.</span></h2>
          <ol className="mt-8 space-y-4">
            {[
              { n: "01", title: "Your manufacturer partner adds you to their network", body: "A manufacturer you already work with uploads their approved dealer list. You're in." },
              { n: "02", title: "Contractor requests land in your inbox", body: "When a contractor in your territory asks about that manufacturer's product, we email your team immediately." },
              { n: "03", title: "You quote directly", body: "Contact the contractor, quote, close. LumberLoop USA stays out of the way — but tracks the outcome for your manufacturer partner." },
            ].map(s => (
              <li key={s.n} className="brutal-card bg-white p-5 flex gap-4">
                <div className="font-mono-ll text-[10px] tracking-[0.3em] uppercase text-slate-500 pt-1">Step {s.n}</div>
                <div>
                  <div className="font-heading font-bold uppercase text-[#0F2540]">{s.title}</div>
                  <p className="mt-1 text-sm text-slate-700">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* What we DON'T do */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="label-tag">Channel Promise</div>
        <h2 className="font-heading text-3xl md:text-4xl font-black uppercase mt-2 text-[#1A365D]">
          What LumberLoop USA <span className="text-[#C62828]">will never do.</span>
        </h2>
        <ul className="mt-6 space-y-3">
          {[
            "Sell products directly to contractors",
            "Show contractors LumberLoop USA pricing",
            "Compete with your dealer counter",
            "Charge dealers to participate",
            "Route your customers to a competitor of the manufacturer they asked about",
          ].map(it => (
            <li key={it} className="flex items-start gap-3 text-slate-700"><CheckCircle2 size={20} className="text-[#C62828] mt-0.5 flex-shrink-0"/><span>{it}</span></li>
          ))}
        </ul>
      </section>

      <section className="bg-[#0F2540] text-white py-20 border-t-2 border-[#1A365D] text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-heading text-3xl md:text-5xl font-black uppercase">Want in? <span className="text-[#C62828]">Talk to your manufacturer partner.</span></h2>
          <p className="mt-3 text-slate-300">Or reach out to us — we'll help make the introduction.</p>
          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2">Contact LumberLoop USA <ArrowRight size={16}/></Link>
            <Link to="/dealers" className="btn-ghost-light inline-flex items-center gap-2">See Existing Dealer Network</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
